import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CATEGORIES, Category, COMPANY_DIVISIONS, CompanySector } from '../data';

export interface SiteContent {
  heroPrimaryHook: string;
  heroSubHook: string;
  globalSupportNumber: string;
  targetDirective: string;
  statusIndicator: string;
  categories: Category[];
  companyDivisions: CompanySector[];
}

interface SiteContentContextType {
  content: SiteContent;
  setContent: React.Dispatch<React.SetStateAction<SiteContent>>;
}

const defaultContent: SiteContent = {
  heroPrimaryHook: 'Digital infrastructure for NexoraX commerce and secure coordination',
  heroSubHook: 'We build advanced marketplaces, automation systems, and high-trust digital environments for organizations.',
  globalSupportNumber: '08000 622827',
  targetDirective: 'All new enterprise partners must configure their infrastructure according to Node-14 documentation.',
  statusIndicator: 'all-nominal',
  categories: CATEGORIES,
  companyDivisions: COMPANY_DIVISIONS,
};

const getInitialContent = (): SiteContent => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('nexorax_site_content');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          // Merge categories to make sure new categories are always updated and fully populated
          const mergedCategories: Category[] = (parsed.categories || []).map((c: any) => {
            const freshCat = CATEGORIES.find(fc => fc.id === c.id);
            if (freshCat) {
              return {
                ...c,
                nodes: freshCat.nodes,
                title: freshCat.title,
                protocol: freshCat.protocol || c.protocol
              };
            }
            return c;
          });
          CATEGORIES.forEach(cat => {
            if (!mergedCategories.some(c => c.id === cat.id)) {
              mergedCategories.push(cat);
            }
          });
          
          // Merge company divisions to ensure edits are preserved while keeping static structural definitions updated
          const mergedDivisions: CompanySector[] = (parsed.companyDivisions || []).map((d: any) => {
            const freshSec = COMPANY_DIVISIONS.find(fd => fd.id === d.id);
            if (freshSec) {
              return {
                ...d,
                title: freshSec.title,
                node: freshSec.node,
                owner: freshSec.owner,
                description: freshSec.description,
                metric: freshSec.metric,
                color: freshSec.color,
                iconName: freshSec.iconName
              };
            }
            return d;
          });
          COMPANY_DIVISIONS.forEach(sec => {
            if (!mergedDivisions.some(d => d.id === sec.id)) {
              mergedDivisions.push(sec);
            }
          });
          
          return {
            ...defaultContent,
            ...parsed,
            categories: mergedCategories,
            companyDivisions: mergedDivisions
          };
        }
      } catch (e) {
        console.error("Error hydrating site content from storage:", e);
      }
    }
  }
  return defaultContent;
};

const SiteContentContext = createContext<SiteContentContextType | undefined>(undefined);

export const SiteContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<SiteContent>(getInitialContent);

  React.useEffect(() => {
    try {
      localStorage.setItem('nexorax_site_content', JSON.stringify(content));
    } catch (e) {
      console.error("Error persisting site content to storage:", e);
    }
  }, [content]);

  return (
    <SiteContentContext.Provider value={{ content, setContent }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (context === undefined) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};
